import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { apiGet, apiSend, getAccessToken } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { SupportAttachment } from '../components/SupportAttachment';
import { SupportEmojiPicker } from '../components/SupportEmojiPicker';
import { SUPPORT_TOPICS } from '../support/azOperatorNames';
import { getTelegramWebApp } from '../telegram/webapp';

interface Message {
  id: number;
  sender_role: string;
  sender_name?: string | null;
  body: string;
  created_at: string;
  attachment_name: string | null;
  attachment_url?: string | null;
  attachment_type?: string | null;
}

interface Conversation {
  public_id: string | null;
  status: string;
  rating: number | null;
  can_rate: boolean;
  contact_email?: string | null;
  contact_phone?: string | null;
  contact_name?: string | null;
  category?: string | null;
  topic_label?: string | null;
  operator_online?: boolean;
  operator_connected?: boolean;
  assigned_name?: string | null;
  assigned_avatar?: string | null;
  created_at?: string | null;
}

const FILE_ACCEPT =
  'image/jpeg,image/png,image/webp,video/mp4,video/webm,application/pdf,text/plain,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.doc,.docx,.xls,.xlsx,.txt';

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString();
}

function playNotify() {
  try {
    const ctx = new window.AudioContext();
    void ctx.resume().then(() => {
      [0, 0.16].forEach((delay) => {
        const oscillator = ctx.createOscillator();
        const gain = ctx.createGain();
        oscillator.type = 'sine';
        oscillator.frequency.value = 880;
        gain.gain.setValueAtTime(0.0001, ctx.currentTime + delay);
        gain.gain.exponentialRampToValueAtTime(0.12, ctx.currentTime + delay + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + delay + 0.14);
        oscillator.connect(gain);
        gain.connect(ctx.destination);
        oscillator.start(ctx.currentTime + delay);
        oscillator.stop(ctx.currentTime + delay + 0.15);
      });
      window.setTimeout(() => void ctx.close(), 700);
    });
  } catch {
    // Brauzer avtomatik səsə icazə verməyə bilər.
  }
}

function isPhotoAvatar(avatar?: string | null): boolean {
  return Boolean(avatar && (avatar.startsWith('/') || avatar.startsWith('http') || avatar.startsWith('blob:')));
}

function OperatorMark({ avatar, name }: { avatar?: string | null; name?: string | null }) {
  const letter = (name || 'V').slice(0, 1).toUpperCase();
  const [photo, setPhoto] = useState<string | null>(null);
  useEffect(() => {
    if (!isPhotoAvatar(avatar) || !avatar) {
      setPhoto(null);
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;
    const token = getAccessToken();
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    void fetch(avatar, { headers, credentials: 'same-origin', cache: 'no-store' })
      .then((response) => (response.ok ? response.blob() : null))
      .then((blob) => {
        if (!blob || cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setPhoto(objectUrl);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [avatar]);
  if (photo) {
    return <img className="support-op-avatar is-photo" src={photo} alt="" />;
  }
  return (
    <span className={`support-op-avatar is-${avatar === 'female' ? 'girl' : avatar === 'male' ? 'boy' : 'neutral'}`} aria-hidden="true">
      {avatar === 'female' ? '👩🏻' : avatar === 'male' ? '👨🏻' : letter}
    </span>
  );
}

export function SupportPage() {
  const { t } = useTranslation(['common', 'support']);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Message[]>([]);
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [uploadPct, setUploadPct] = useState<number | null>(null);
  const [failed, setFailed] = useState<{ text: string; file: File | null } | null>(null);
  const [attachmentMenuOpen, setAttachmentMenuOpen] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [cameraHelp, setCameraHelp] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [stars, setStars] = useState(5);
  const [ratingComment, setRatingComment] = useState('');
  const [ratingOpen, setRatingOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [chatMenuOpen, setChatMenuOpen] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(() => window.localStorage.getItem('vbb_support_sound') !== 'off');
  const [formName, setFormName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formTopic, setFormTopic] = useState('');
  const [formMessage, setFormMessage] = useState('');
  const [formConsent, setFormConsent] = useState(false);
  const [searchPhase, setSearchPhase] = useState<'searching' | 'waiting' | 'connected'>('searching');
  const photoRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const threadRef = useRef<HTMLDivElement>(null);
  const lastOperatorMessageId = useRef<number | null>(null);
  const lastMessageId = useRef(0);
  const messagesInitialized = useRef(false);
  const soundEnabledRef = useRef(soundEnabled);
  const startingRef = useRef(false);
  soundEnabledRef.current = soundEnabled;

  const hasOpenChat = conversation?.status === 'open' && conversation.public_id;

  const applyViewport = useCallback(() => {
    const tg = getTelegramWebApp();
    const height = tg?.viewportStableHeight || window.visualViewport?.height || window.innerHeight;
    document.documentElement.style.setProperty('--support-vh', `${Math.round(height)}px`);
  }, []);

  const leaveChat = useCallback(() => {
    const tg = getTelegramWebApp();
    try { tg?.BackButton?.hide(); } catch { /* ignore */ }
    if (window.history.length > 1) navigate(-1);
    else navigate('/');
  }, [navigate]);

  useEffect(() => {
    window.localStorage.setItem('vbb_support_sound', soundEnabled ? 'on' : 'off');
  }, [soundEnabled]);

  useEffect(() => {
    if (!user) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    applyViewport();
    window.visualViewport?.addEventListener('resize', applyViewport);
    window.addEventListener('resize', applyViewport);
    const tg = getTelegramWebApp();
    const onBack = () => leaveChat();
    try {
      tg?.BackButton?.show();
      tg?.BackButton?.onClick(onBack);
    } catch { /* host dəstəkləməyə bilər */ }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') leaveChat();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      document.documentElement.style.removeProperty('--support-vh');
      window.visualViewport?.removeEventListener('resize', applyViewport);
      window.removeEventListener('resize', applyViewport);
      window.removeEventListener('keydown', onKey);
      try {
        tg?.BackButton?.offClick(onBack);
        tg?.BackButton?.hide();
      } catch { /* ignore */ }
    };
  }, [user, applyViewport, leaveChat]);

  const mergeMessages = useCallback((rows: Message[]) => {
    const latestOperatorId = rows.filter((m) => m.sender_role === 'admin').reduce((max, m) => Math.max(max, m.id), 0);
    if (messagesInitialized.current && latestOperatorId > (lastOperatorMessageId.current ?? 0) && soundEnabledRef.current) {
      playNotify();
    }
    lastOperatorMessageId.current = latestOperatorId || lastOperatorMessageId.current;
    lastMessageId.current = rows.reduce((max, item) => Math.max(max, item.id), lastMessageId.current);
    messagesInitialized.current = true;
    setMessages((prev) => {
      if (!rows.length) return prev;
      const byId = new Map(prev.map((item) => [item.id, item]));
      for (const row of rows) byId.set(row.id, row);
      return [...byId.values()].sort((a, b) => a.id - b.id);
    });
  }, []);

  const load = useCallback(async (afterId = 0) => {
    const [rows, conv] = await Promise.all([
      apiGet<Message[]>(`/api/v1/support/conversation/messages${afterId ? `?after_id=${afterId}` : ''}`),
      apiGet<Conversation>('/api/v1/support/conversation'),
    ]);
    if (!afterId || rows.length) mergeMessages(rows);
    setConversation(conv);
    setFormName((current) => current || conv.contact_name || user?.display_name || user?.username || '');
    setFormEmail((current) => current || conv.contact_email || user?.email || '');
    return conv;
  }, [mergeMessages, user?.display_name, user?.email, user?.username]);

  useEffect(() => {
    if (!user) return;
    void load().catch(() => setError(t('support:loadFailed')));
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        void load(messagesInitialized.current ? lastMessageId.current : 0).catch(() => undefined);
      }
    }, 1600);
    return () => window.clearInterval(timer);
  }, [user?.public_id, load]);

  useEffect(() => {
    if (!user) return;
    const token = getAccessToken();
    if (!token || !window.WebSocket) return;
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    let socket: WebSocket | null = null;
    let closed = false;
    let retry = 800;
    const connect = () => {
      if (closed) return;
      socket = new WebSocket(`${proto}://${window.location.host}/api/v1/support/ws?token=${encodeURIComponent(token)}`);
      socket.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data) as { type?: string; message?: Message; operator_online?: boolean };
          if (payload.type === 'message' && payload.message) mergeMessages([payload.message]);
          if (typeof payload.operator_online === 'boolean') {
            setConversation((prev) => (prev ? { ...prev, operator_online: payload.operator_online } : prev));
          }
        } catch {
          // polling bərpa edəcək
        }
      };
      socket.onclose = () => {
        if (closed) return;
        window.setTimeout(connect, retry);
        retry = Math.min(retry * 1.6, 8000);
      };
    };
    connect();
    return () => {
      closed = true;
      socket?.close();
    };
  }, [user?.public_id, mergeMessages]);

  useEffect(() => {
    threadRef.current?.scrollTo({ top: threadRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages.length, emojiOpen]);

  useEffect(() => {
    if (conversation?.operator_connected && conversation.assigned_name) {
      setSearchPhase('connected');
      return;
    }
    if (conversation?.status !== 'open') return;
    const created = conversation.created_at ? Date.parse(conversation.created_at) : Date.now();
    if (Date.now() - created > 5500) {
      setSearchPhase('waiting');
      return;
    }
    setSearchPhase('searching');
    const timer = window.setTimeout(() => {
      setSearchPhase((current) => (current === 'connected' ? current : 'waiting'));
    }, 5500);
    return () => window.clearTimeout(timer);
  }, [conversation?.operator_connected, conversation?.assigned_name, conversation?.status, conversation?.created_at]);

  function sendPayload(text: string, file?: File | null) {
    if (file && file.size > 50 * 1024 * 1024) {
      setError(t('support:fileTooLarge'));
      return;
    }
    setBusy(true);
    setError(null);
    setFailed(null);
    const form = new FormData();
    form.append('body', text);
    if (file) form.append('file', file);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', '/api/v1/support/conversation/messages');
    xhr.responseType = 'json';
    const token = getAccessToken();
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.setRequestHeader('Accept', 'application/json');
    if (file) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) setUploadPct(Math.round((event.loaded / event.total) * 100));
      };
    }
    xhr.onload = () => {
      setBusy(false);
      setUploadPct(null);
      if (xhr.status >= 200 && xhr.status < 300) {
        const created = xhr.response as Message | null;
        if (created?.id) mergeMessages([created]);
        setBody('');
        setSelectedFile(null);
        [photoRef, videoRef, fileRef, cameraRef].forEach((ref) => {
          if (ref.current) ref.current.value = '';
        });
        void load(created?.id ?? 0).catch(() => undefined);
        return;
      }
      setFailed({ text, file: file ?? null });
      setError(xhr.status === 413 ? t('support:fileTooLarge') : t('support:sendFailed', { status: String(xhr.status) }));
    };
    xhr.onerror = () => {
      setBusy(false);
      setUploadPct(null);
      setFailed({ text, file: file ?? null });
      setError(t('support:networkError'));
    };
    xhr.send(form);
  }

  function onSend(event: FormEvent) {
    event.preventDefault();
    if (!body.trim() && !selectedFile) return;
    sendPayload(body.trim(), selectedFile);
  }

  async function onStart(event: FormEvent) {
    event.preventDefault();
    if (startingRef.current || busy) return;
    if (!formName.trim()) { setError(t('support:nameRequired')); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formEmail.trim())) { setError(t('support:emailInvalid')); return; }
    if (!formTopic) { setError(t('support:topicRequired')); return; }
    if (!formMessage.trim()) { setError(t('support:messageRequired')); return; }
    if (!formConsent) { setError(t('support:consentRequired')); return; }
    startingRef.current = true;
    setBusy(true);
    setError(null);
    try {
      const conv = await apiSend<Conversation>('POST', '/api/v1/support/conversation/start', {
        name: formName.trim(),
        email: formEmail.trim(),
        topic: formTopic,
        message: formMessage.trim(),
        consent: true,
      });
      setConversation(conv);
      setSearchPhase('searching');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('support:requestFailed'));
    } finally {
      setBusy(false);
      startingRef.current = false;
    }
  }

  const dated = useMemo(() => {
    let last = '';
    return messages.map((message) => {
      const day = formatDate(message.created_at);
      const show = day && day !== last;
      last = day;
      return { message, day: show ? day : null };
    });
  }, [messages]);

  if (!user) {
    return (
      <div className="page support-gate">
        <article className="support-prechatsheet">
          <h1>{t('common:liveSupport')}</h1>
          <p>{t('support:loginRequired')}</p>
          <Link to="/auth/login">{t('common:login')}</Link>
        </article>
      </div>
    );
  }

  return (
    <div className="page support-shell">
      {!hasOpenChat ? (
        <section className="support-intake">
          <header className="support-intake-head">
            <button type="button" className="support-head-button" aria-label={t('support:back')} onClick={leaveChat}>‹</button>
            <div>
              <h1>{t('support:brandTitle')}</h1>
              <p>{t('support:intakeHint')}</p>
            </div>
            <button type="button" className="support-head-button" aria-label={t('support:close')} onClick={leaveChat}>×</button>
          </header>
          <form className="support-intake-form" onSubmit={(e) => void onStart(e)}>
            <label>{t('support:yourName')}
              <input value={formName} onChange={(e) => setFormName(e.target.value)} required minLength={2} maxLength={80} placeholder={t('support:yourName')} />
            </label>
            <label>{t('support:yourEmail')}
              <input type="email" value={formEmail} onChange={(e) => setFormEmail(e.target.value)} required placeholder={t('support:emailPlaceholder')} />
            </label>
            <label>{t('support:topicLabel')}
              <select value={formTopic} onChange={(e) => setFormTopic(e.target.value)} required>
                <option value="">{t('support:topicSelect')}</option>
                {SUPPORT_TOPICS.map((topic) => (
                  <option key={topic.id} value={topic.id}>{t(`support:topic_${topic.id}`)}</option>
                ))}
              </select>
            </label>
            <label>{t('support:messageLabel')}
              <textarea value={formMessage} onChange={(e) => setFormMessage(e.target.value)} required minLength={2} rows={4} placeholder={t('support:messagePlaceholder')} />
            </label>
            <label className="support-consent">
              <input type="checkbox" checked={formConsent} onChange={(e) => setFormConsent(e.target.checked)} />
              {t('support:consentLong')}
            </label>
            {error ? <p className="support-rating-error" role="alert">{error}</p> : null}
            <button className="support-rating-submit" type="submit" disabled={busy}>{busy ? t('support:sending') : t('support:startLive')}</button>
          </form>
        </section>
      ) : (
        <div className="support-chat-page">
          <header className="support-chat-head">
            <button type="button" className="support-head-button" aria-label={t('support:back')} onClick={leaveChat}>‹</button>
            <div className="support-operator-identity">
              <OperatorMark avatar={conversation?.assigned_avatar} name={conversation?.assigned_name} />
              <div>
                <h1>{conversation?.assigned_name || t('support:brandShort')}</h1>
                <small>
                  <span className={`support-online-dot${conversation?.operator_online === false ? ' is-off' : ''}`} />
                  {conversation?.assigned_name ? t('support:liveLabel') : conversation?.operator_online === false ? t('support:offline') : t('support:online')}
                </small>
              </div>
            </div>
            <div className="support-head-actions">
              <button type="button" className="support-head-button" aria-label={t('support:moreOptions')} aria-expanded={chatMenuOpen} onClick={() => { setChatMenuOpen((v) => !v); setAttachmentMenuOpen(false); }}>⋮</button>
              <button type="button" className="support-head-button" aria-label={t('support:closeChat')} onClick={leaveChat}>×</button>
              {chatMenuOpen && (
                <>
                  <button type="button" className="support-menu-backdrop" aria-label={t('support:closeMenu')} onClick={() => setChatMenuOpen(false)} />
                  <div className="support-options-menu">
                    <button type="button" onClick={() => { setSoundEnabled(!soundEnabled); setChatMenuOpen(false); }}>
                      {soundEnabled ? t('support:soundOff') : t('support:soundOn')}
                    </button>
                    <button type="button" onClick={() => { setChatMenuOpen(false); setRatingOpen(true); }}>{t('support:rateOperator')}</button>
                    <button type="button" onClick={() => { setChatMenuOpen(false); setContactOpen(true); }}>{t('support:editContact')}</button>
                  </div>
                </>
              )}
            </div>
          </header>
          <div className="support-thread" ref={threadRef}>
            {searchPhase === 'searching' && !conversation?.assigned_name ? (
              <div className="support-status-line"><span className="support-spinner" /> {t('support:searchingOperator')}</div>
            ) : null}
            {searchPhase === 'waiting' && !conversation?.assigned_name ? (
              <div className="support-status-line">{t('support:waitingOperator')}</div>
            ) : null}
            {conversation?.assigned_name ? (
              <div className="support-status-line">{t('support:operatorJoined', { name: conversation.assigned_name })}</div>
            ) : null}
            {dated.map(({ message: m, day }) => (
              <div key={m.id} className="support-msg-wrap">
                {day ? <div className="support-date-chip">{day}</div> : null}
                {m.sender_role === 'system' ? (
                  <div className="support-status-line">{m.body}</div>
                ) : (
                  <article className={`support-bubble ${m.sender_role === 'user' ? 'mine' : 'ops'}`}>
                    {m.sender_role === 'admin' && conversation?.assigned_name ? (
                      <span className="support-bubble-who">
                        <OperatorMark avatar={conversation.assigned_avatar} name={conversation.assigned_name} />
                        <span className="support-sender">{m.sender_name || conversation.assigned_name}</span>
                      </span>
                    ) : m.sender_role !== 'user' ? (
                      <span className="support-sender">{m.sender_name || t('support:operator')}</span>
                    ) : null}
                    {m.body && m.body !== '(attachment)' && m.body !== '📎' ? <p>{m.body}</p> : null}
                    {m.attachment_name && m.attachment_url ? (
                      <SupportAttachment url={m.attachment_url} name={m.attachment_name} type={m.attachment_type} />
                    ) : m.attachment_name ? <small>{m.attachment_name}</small> : null}
                    <time>
                      {formatTime(m.created_at)}
                      {m.sender_role === 'user' ? <span className="support-ticks"> ✓✓</span> : null}
                    </time>
                  </article>
                )}
              </div>
            ))}
          </div>
          {selectedFile && (
            <div className="support-selected-file">
              <span>📎 {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(1)} MB)</span>
              <button type="button" onClick={() => setSelectedFile(null)} aria-label={t('support:removeFile')}>×</button>
            </div>
          )}
          {uploadPct !== null && <div className="support-upload-bar" role="progressbar" aria-valuenow={uploadPct}><span style={{ width: `${uploadPct}%` }} /></div>}
          {failed && (
            <div className="support-retry-bar">
              <span>{t('support:sendFailedShort')}</span>
              <button type="button" onClick={() => sendPayload(failed.text, failed.file)}>{t('support:retry')}</button>
            </div>
          )}
          {emojiOpen && <SupportEmojiPicker onPick={(emoji) => setBody((current) => current + emoji)} />}
          <form className="support-composer" onSubmit={(e) => void onSend(e)}>
            <div className="support-attach-wrap">
              <button type="button" className="support-attach-button" aria-label={t('support:attachFile')} onClick={() => { setAttachmentMenuOpen(!attachmentMenuOpen); setEmojiOpen(false); setChatMenuOpen(false); }}>＋</button>
              {attachmentMenuOpen && (
                <div className="support-attach-menu">
                  <button type="button" onClick={() => { setAttachmentMenuOpen(false); setCameraHelp(true); }}>{t('support:cameraShot')}</button>
                  <button type="button" onClick={() => { setAttachmentMenuOpen(false); photoRef.current?.click(); }}>{t('support:pickPhoto')}</button>
                  <button type="button" onClick={() => { setAttachmentMenuOpen(false); videoRef.current?.click(); }}>{t('support:pickVideo')}</button>
                  <button type="button" onClick={() => { setAttachmentMenuOpen(false); fileRef.current?.click(); }}>{t('support:pickFile')}</button>
                </div>
              )}
            </div>
            <input ref={photoRef} type="file" hidden accept="image/jpeg,image/png,image/webp" onChange={(e) => setSelectedFile(e.target.files?.[0] || null)} />
            <input ref={videoRef} type="file" hidden accept="video/mp4,video/webm" onChange={(e) => setSelectedFile(e.target.files?.[0] || null)} />
            <input ref={fileRef} type="file" hidden accept={FILE_ACCEPT} onChange={(e) => setSelectedFile(e.target.files?.[0] || null)} />
            <input ref={cameraRef} type="file" hidden accept="image/*" capture="environment" onChange={(e) => setSelectedFile(e.target.files?.[0] || null)} />
            <input className="field-input" value={body} onChange={(e) => setBody(e.target.value)} placeholder={t('support:textPlaceholder')} aria-label={t('support:messageAria')} />
            <button type="button" className="support-emoji-button" aria-label={t('support:emojiAria')} onClick={() => { setEmojiOpen(!emojiOpen); setAttachmentMenuOpen(false); }}>☺</button>
            <button type="submit" className="support-send-button" aria-label={t('support:sendAria')} disabled={busy || (!body.trim() && !selectedFile)}>➤</button>
          </form>
          {error ? <p className="muted-note">{error}</p> : null}
        </div>
      )}
      {ratingOpen && (
        <div className="support-rating-overlay" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setRatingOpen(false); }}>
          <section className="support-rating-sheet" role="dialog" aria-modal="true">
            <button className="support-rating-close" type="button" aria-label={t('support:close')} onClick={() => setRatingOpen(false)}>×</button>
            <h2>{t('support:rateTitle')}</h2>
            <form onSubmit={async (event) => {
              event.preventDefault();
              setBusy(true);
              try {
                await apiSend('POST', '/api/v1/support/conversation/rate', { stars, comment: ratingComment.trim() || undefined });
                await load();
                setRatingOpen(false);
              } catch { setError(t('support:ratingFailed')); }
              finally { setBusy(false); }
            }}>
              <div className="support-stars">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" className={n <= stars ? 'is-selected' : ''} onClick={() => setStars(n)}>★</button>
                ))}
              </div>
              <textarea className="support-rating-comment" value={ratingComment} onChange={(e) => setRatingComment(e.target.value)} placeholder={t('support:ratingPlaceholder')} rows={3} />
              <button className="support-rating-submit" type="submit" disabled={busy}>{t('support:submitRate')}</button>
            </form>
          </section>
        </div>
      )}
      {contactOpen && (
        <div className="support-rating-overlay" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setContactOpen(false); }}>
          <section className="support-rating-sheet support-contact-sheet" role="dialog" aria-modal="true">
            <button className="support-rating-close" type="button" aria-label={t('support:close')} onClick={() => setContactOpen(false)}>×</button>
            <h2>{t('support:editContactTitle')}</h2>
            <form onSubmit={async (event) => {
              event.preventDefault();
              try {
                await apiSend('POST', '/api/v1/support/conversation/contact', {
                  name: formName.trim() || undefined,
                  email: formEmail.trim() || undefined,
                  consent: true,
                });
                await load();
                setContactOpen(false);
              } catch { setError(t('support:contactSaveFailed')); }
            }}>
              <input className="field-input" value={formName} onChange={(e) => setFormName(e.target.value)} placeholder={t('support:namePlaceholder')} />
              <input className="field-input" value={formEmail} onChange={(e) => setFormEmail(e.target.value)} placeholder={t('common:supportEmail')} type="email" />
              <button className="support-rating-submit" type="submit">{t('support:save')}</button>
            </form>
          </section>
        </div>
      )}
      {cameraHelp && (
        <div className="support-rating-overlay" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setCameraHelp(false); }}>
          <section className="support-rating-sheet" role="dialog" aria-modal="true">
            <h2>{t('support:cameraPermissionTitle')}</h2>
            <p>{t('support:cameraPermissionBody')}</p>
            <button className="support-rating-submit" type="button" onClick={() => { setCameraHelp(false); cameraRef.current?.click(); }}>{t('support:allowCamera')}</button>
            <button className="support-rating-submit" type="button" style={{ marginTop: 8, background: '#8aa0b3' }} onClick={() => setCameraHelp(false)}>{t('support:notNow')}</button>
          </section>
        </div>
      )}
    </div>
  );
}
